/**
 * The single door to the API.
 *
 * Same-origin by design (one container serves both), so there is no base URL to
 * configure and no CORS to negotiate. Phase 5 attaches the Clerk bearer token
 * here — one place, so no call site can forget.
 */

import { getToken } from "@/lib/clerk";

export type DenyReason =
  | "unauthenticated"
  | "forbidden_domain"
  | "deactivated"
  | "not_registered"
  | "no_email"
  | "forbidden";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly reason: DenyReason | null,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /**
   * True when the caller's session is unusable and the app should sign them out
   * rather than retry — as opposed to a plain permission error on one action.
   */
  get requiresSignOut(): boolean {
    return (
      this.reason === "unauthenticated" ||
      this.reason === "forbidden_domain" ||
      this.reason === "deactivated" ||
      this.reason === "not_registered"
    );
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // ⚠ One place attaches the token, so no call site can forget. A null token
  // means no active session; the request goes out unauthenticated and the API
  // answers 401, which the gate turns into a sign-in.
  const token = await getToken();

  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (!res.ok) {
    // Error bodies are JSON by contract; a non-JSON body means something in
    // front of the app (a proxy, an outage page) answered instead.
    const body = (await res.json().catch(() => null)) as {
      error?: string;
      reason?: DenyReason;
    } | null;
    throw new ApiError(
      res.status,
      body?.reason ?? null,
      body?.error ?? `Request failed (${res.status})`,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
